import { Button, Icon } from "@blueprintjs/core";
import React, { useEffect, useRef, useState } from "react";

/**
 * Centralized Undo/Redo for the canvas editor.
 * Listens for: object:modified (Fabric), canvas:state-changed (programmatic), background:changed, text events.
 * Any component that modifies canvas state (add/remove/set) must fire canvas.fire("canvas:state-changed") after the change so history is updated.
 */
export function UndoRedoComponent(editor) {
  const { Canvaseditor, activePage, templateID, captureInitialStateRef } = editor;

  const pageHistories = useRef({});
  const isInitialized = useRef({});
  const editingObject = useRef(null); // Track the object being edited
  const prevTemplateIdRef = useRef(templateID);

  const [isUndoDisabled, setIsUndoDisabled] = useState(true);
  const [isRedoDisabled, setIsRedoDisabled] = useState(true);

  // Throttle function to avoid excessive history saves during rapid typing
  const throttle = (func, limit) => {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  };

  // Deep-clone state so we never mutate shared refs; keeps history immutable.
  const cloneState = (state) => {
    if (!state) return null;
    try {
      return JSON.parse(JSON.stringify(state));
    } catch (e) {
      return null;
    }
  };

  // Save the current state of the canvas to the history (immutable snapshot).
  const saveHistory = throttle(() => {
    if (!Canvaseditor || !activePage) return;

    const pageState = pageHistories.current[activePage];
    if (!pageState || !Array.isArray(pageState.history)) return;

    const rawState = Canvaseditor.toJSON();
    const snapshot = cloneState(rawState);
    if (!snapshot) return;

    // Filter out `fabric.Line` objects before saving (mutate only the clone)
    if (Array.isArray(snapshot.objects)) {
      snapshot.objects = snapshot.objects.filter((obj) => obj.type !== "line");
    }

    // Avoid saving duplicate states
    if (
      pageState.history.length > 0 &&
      JSON.stringify(pageState.history[pageState.step]) === JSON.stringify(snapshot)
    ) {
      return;
    }

    // Trim future history if in the middle of the stack
    pageState.history = pageState.history.slice(0, pageState.step + 1);

    // Add immutable snapshot to history
    pageState.history.push(snapshot);
    pageState.step++;

    updateButtonStates();
  }, 200); // Throttle saves to once every 200ms

  // Restore a state from the history
  const restoreHistory = (step) => {
    if (!Canvaseditor || !activePage) return;

    const pageState = pageHistories.current[activePage];
    if (step < 0 || step >= pageState.history.length) {
      return;
    }

    // Restore from a clone so loadFromJSON cannot mutate stored history
    const stateToRestore = cloneState(pageState.history[step]);
    if (!stateToRestore) return;

    // Load state excluding `fabric.Line` objects
    Canvaseditor.loadFromJSON(stateToRestore, () => {
      Canvaseditor.getObjects().forEach((object) => {
        if (object instanceof fabric.Group) {
          // Check if all objects in the group are instances of fabric.Line.
          const allLines = object
            .getObjects()
            .every((obj) => obj instanceof fabric.Line);

          if (allLines) {
            object.selectable = false; // Make the line non-selectable
            object.evented = false; // Disable events on the line
            // Canvaseditor.remove(object);
          }
        }
      });

      Canvaseditor.renderAll();
      if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("canvas:restored");
    });

    updateButtonStates();
  };

  // Undo action
  const undo = () => {
    if (!activePage) return;

    const pageState = pageHistories.current[activePage];
    if (!pageState || pageState.step <= 0) return;

    const targetStep = pageState.step - 1;

    // Guard against undoing back to an accidentally empty baseline.
    // If the earliest history entry (index 0) has no objects but there are
    // later states, treat index 1 as the true "initial" state and do not
    // restore the empty canvas.
    if (
      targetStep === 0 &&
      pageState.history.length > 1 &&
      pageState.history[0] &&
      Array.isArray(pageState.history[0].objects) &&
      pageState.history[0].objects.length === 0
    ) {
      // Clamp at the first non-empty state (index 1) and update buttons.
      pageState.step = 1;
      updateButtonStates();
      return;
    }

    pageState.step = targetStep;
    restoreHistory(pageState.step);
  };

  // Redo action
  const redo = () => {
    if (!activePage) return;

    const pageState = pageHistories.current[activePage];
    if (pageState.step < pageState.history.length - 1) {
      pageState.step++;
      restoreHistory(pageState.step);
    }
  };

  // Update undo/redo button states
  const updateButtonStates = () => {
    if (!activePage) {
      setIsUndoDisabled(true);
      setIsRedoDisabled(true);
      return;
    }

    const pageState = pageHistories.current[activePage];
    setIsUndoDisabled(!pageState || pageState.step <= 0);
    setIsRedoDisabled(
      !pageState || pageState.step >= pageState.history.length - 1
    );
  };

  // Save the initial state for a specific page (called when that page's canvas has finished loading).
  // Used so we capture state *after* loadFromJSON completes. Store an immutable snapshot.
  const saveInitialStateForPage = (canvas, pageIndex) => {
    if (!canvas || !pageIndex) return;

    const rawState = canvas.toJSON();
    const snapshot = cloneState(rawState);
    if (!snapshot) return;

    if (Array.isArray(snapshot.objects)) {
      snapshot.objects = snapshot.objects.filter((obj) => obj.type !== "line");
    }

    pageHistories.current[pageIndex] = {
      history: [snapshot],
      step: 0,
    };
    isInitialized.current[pageIndex] = true;
    updateButtonStates();
  };

  // Handle text changes incrementally
  const handleTextChanges = throttle((e) => {
    if (!Canvaseditor || !activePage) return;

    const target = e.target;
    if (target && target.type === "textbox") {
      saveHistory(); // Save history incrementally for text changes
    }
  }, 200);

  // Handle text changes incrementally
  const handleTextAreaChanges = throttle((e) => {
    if (!Canvaseditor || !activePage) return;
    const target = e.target;
    if (target) {
      saveHistory(); // Save history incrementally for text changes
    }
  }, 200);

  // Save the initial state of the text before editing starts
  const handleTextEditStart = (e) => {
    editingObject.current = e.target;
    saveHistory(); // Save the initial state before editing starts
  };

  // Save the final state of the text after editing stops
  const handleTextEditStop = () => {
    if (editingObject.current) {
      saveHistory(); // Save the final state after editing stops
      editingObject.current = null;
    }
  };

  // When a new template is selected, reset undo/redo history so that undo
  // does not restore the previous template. The new template becomes the
  // initial state once the canvas has loaded (handled by the effect below).
  useEffect(() => {
    if (templateID === undefined || templateID === null) return;

    const prevId = prevTemplateIdRef.current;
    prevTemplateIdRef.current = templateID;

    // Reset only when template actually changed (e.g. user selected another template)
    if (prevId !== templateID) {
      pageHistories.current = {};
      isInitialized.current = {};
      setIsUndoDisabled(true);
      setIsRedoDisabled(true);
    }
  }, [templateID]);

  // Expose capture so CanvasArea can call it when each page's canvas is ready (after loadFromJSON).
  useEffect(() => {
    if (captureInitialStateRef) {
      captureInitialStateRef.current = saveInitialStateForPage;
    }
    return () => {
      if (captureInitialStateRef) captureInitialStateRef.current = null;
    };
  }, [captureInitialStateRef]);

  // Setup canvas event listeners (initial state is captured via onPageCanvasReady, not here).
  // "object:modified" = Fabric native (e.g. move/resize). "canvas:state-changed" = any programmatic change (add/remove/set).
  useEffect(() => {
    if (Canvaseditor && activePage) {
      Canvaseditor.on("object:modified", saveHistory);
      Canvaseditor.on("canvas:state-changed", saveHistory);
      Canvaseditor.on("text:editing:entered", handleTextEditStart);
      Canvaseditor.on("text:changed", handleTextChanges);
      Canvaseditor.on("text:editing:exited", handleTextEditStop);
      Canvaseditor.on("background:changed", saveHistory);

      const handleWindowBackgroundChanged = () => saveHistory();
      window.addEventListener("canvas:background:changed", handleWindowBackgroundChanged);

      return () => {
        Canvaseditor.off("object:modified", saveHistory);
        Canvaseditor.off("canvas:state-changed", saveHistory);
        Canvaseditor.off("text:editing:entered", handleTextEditStart);
        Canvaseditor.off("text:changed", handleTextChanges);
        Canvaseditor.off("text:editing:exited", handleTextEditStop);
        Canvaseditor.off("background:changed", saveHistory);
        window.removeEventListener("canvas:background:changed", handleWindowBackgroundChanged);
      };
    }
  }, [Canvaseditor, activePage]);

  useEffect(() => {
    updateButtonStates();
  }, [activePage]);

  useEffect(() => {
    if (!Canvaseditor) return;

    const observer = new MutationObserver((mutationsList) => {
      const edit_texts = document.querySelectorAll(".edit_text");
      edit_texts.forEach((editText) => {
        if (!editText.getAttribute("data-listener-added")) {
          editText.addEventListener("input", handleTextAreaChanges);
          editText.setAttribute("data-listener-added", "true"); // Avoid multiple adding
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Clean up
    return () => observer.disconnect();
  }, [Canvaseditor]);


  // Add keypress handling for undo (Ctrl+Z) and redo (Ctrl+Y).
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key.toLowerCase()) {
          case "z":
            event.preventDefault(); // Prevent browser undo
            undo();
            break;
          case "y":
            event.preventDefault(); // Prevent browser redo
            redo();
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [undo, redo]);

  return (
    <div className="bp4-navbar-group bp4-align-left margin-right-10 gap-5">
      <Button
        onClick={undo}
        icon={<Icon icon="undo" />}
        disabled={isUndoDisabled}
      />
      <Button
        onClick={redo}
        icon={<Icon icon="redo" />}
        disabled={isRedoDisabled}
      />
    </div>
  );
}
